// frontend/src/components/PlatformsNavDropdown.jsx
// Hover mega-menu for the navbar "Platforms" item.
// Reference: Digitail's platform dropdown (centered light panel, grouped columns,
// header + footer rows) — minus the AI assistant widget at the bottom-left.
// Items show title only (one group icon per column header); the full data
// (desc, bubble) lives on /platform.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import {
  PLATFORM_GROUPS,
  PLATFORM_FEATURES,
  PLATFORM_ICON_PATHS,
  getPlatformFeaturesByGroup,
} from "@/config/platformFeatures";

// Strong ease-out so the panel feels responsive the instant it appears.
const PANEL_EASE = [0.23, 1, 0.32, 1];

const FeatureIcon = ({ iconName }) => (
  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-linear-to-br from-[#eaf1ff] to-[#f5f9ff] text-[#2546A1] transition-colors duration-150 group-hover:border-[#2546A1]/30 group-hover:from-[#2546A1] group-hover:to-[#0b3f7a] group-hover:text-white">
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d={PLATFORM_ICON_PATHS[iconName]} />
    </svg>
  </span>
);

export default function PlatformsNavDropdown({ link, hoveredPath, onHoverChange }) {
  const [isOpen, setIsOpen] = useState(false);
  // Grace timer: the panel's top corners (e.g. "Explore all platforms") sit far
  // to the left/right of the trigger, so the pointer briefly crosses sibling
  // nav links on its way there. Without a short delay the dropdown would close
  // mid-travel and those corner links would be unreachable.
  const closeTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const close = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setIsOpen(false);
  }, []);

  const handleEnter = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setIsOpen(true);
    onHoverChange(link.path);
  }, [link.path, onHoverChange]);

  const handleLeave = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      onHoverChange(null);
    }, 250);
  }, [onHoverChange]);

  // Keep the dropdown open while focus moves inside it (keyboard users).
  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      clearTimeout(closeTimerRef.current);
      setIsOpen(false);
      onHoverChange(null);
    }
  };

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={() => setIsOpen(true)}
      onBlur={handleBlur}>
      {/* Trigger — same styling + sliding pill as the other nav links */}
      <Link
        to={link.path}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={close}
        className="relative block px-3 py-2 text-sm font-semibold whitespace-nowrap text-[#23355B] transition-colors duration-300 hover:text-[#23355B]/80 lg:px-4 lg:py-2.5 xl:px-6 xl:py-3 xl:text-lg">
        {hoveredPath === link.path && (
          <motion.div
            layoutId="nav-hover-pill"
            className="absolute inset-0 z-0 rounded-xl bg-[#23355B]/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          />
        )}
        <span className="relative z-10 inline-flex items-center gap-1.5">
          {link.name}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </span>
      </Link>

      {/* Mega-menu panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, transform: "translateY(10px) scale(0.98)" }}
            animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
            exit={{ opacity: 0, transform: "translateY(6px) scale(0.99)" }}
            transition={{ duration: 0.18, ease: PANEL_EASE }}
            style={{ transformOrigin: "top center" }}
            className="absolute top-full left-1/2 z-50 w-[880px] -translate-x-1/2 pt-3 lg:w-[980px] xl:w-[1120px] max-w-[calc(100vw-2rem)]">
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-2xl shadow-slate-900/10">
              {/* Header row */}
              <div className="flex items-center justify-between gap-6 border-b border-slate-100 px-8 pt-6 pb-5">
                <div className="min-w-0">
                  <p className="text-xs font-bold tracking-[0.18em] text-[#1182b7] uppercase">
                    VetAssist Ecosystem
                  </p>
                  <p className="mt-1.5 text-lg font-bold text-[#1A3A5C]">
                    One platform, every part of your clinic.
                  </p>
                </div>
                <Link
                  to="/platform"
                  onClick={close}
                  className="group inline-flex shrink-0 items-center gap-1.5 text-base font-semibold whitespace-nowrap text-[#2546A1] transition-colors duration-150 hover:text-[#1d3782]">
                  Explore all platforms
                  <ArrowRight className="h-5 w-5 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>

              {/* 4 grouped columns — one group icon per column header, titles only */}
              <div className="grid grid-cols-4 gap-x-6 gap-y-3 px-8 py-6">
                {PLATFORM_GROUPS.map((group) => (
                  <div key={group.id} className="min-w-0">
                    <div className="mb-3.5">
                      <Link
                        to={`/platform#${group.id}`}
                        onClick={close}
                        aria-label={`${group.label} features`}
                        className="group block min-w-0">
                        <FeatureIcon iconName={group.icon} />
                        <p className="mt-3 truncate text-xs font-bold tracking-[0.14em] text-slate-400 uppercase transition-colors duration-150 group-hover:text-[#2546A1]">
                          {group.label}
                        </p>
                      </Link>
                    </div>
                    <ul className="space-y-1">
                      {getPlatformFeaturesByGroup(group.id).map((feature) => (
                        <li key={feature.id}>
                          <Link
                            to={`/platform#${feature.id}`}
                            onClick={close}
                            className="group block rounded-lg px-2.5 py-2 transition-colors duration-150 hover:bg-[#eef2ff]">
                            <span className="block min-w-0 truncate text-[15px] font-semibold text-[#23355B] transition-colors duration-150 group-hover:text-[#2546A1]">
                              {feature.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Footer row — digitail has an AI widget bottom-left; deliberately omitted */}
              <div className="flex items-center justify-between gap-6 border-t border-slate-100 bg-slate-50/80 px-8 py-5">
                <p className="min-w-0 text-sm font-medium text-slate-500">
                  {PLATFORM_FEATURES.length}+ tools across {PLATFORM_GROUPS.length}{" "}
                  platforms — one login.
                </p>
                <Link
                  to="/demo"
                  onClick={close}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#2546A1] px-6 py-3 text-[15px] font-bold text-white shadow-md shadow-blue-900/20 transition-colors duration-150 hover:bg-[#1d3782]">
                  Book a Demo
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
